/**
 * FiduciaryGates — Conservatorship / Representative Payee / VA Fiduciary /
 * Medical Set-Aside / USDA Grant.
 *
 * Ported from SPFx ProceduralGates:
 *   - _validateRequiredFields (Conservator / RepPayee / VAFiduciary / MSA / USDA)
 *   - _validateEntityRestrictions (CONSERV_NO_ODP, CONSERV_NO_DEBIT, REPPAYEE_NO_DEBIT)
 *   - _validateRelationships (CONSERV_PROTECTED, REPPAYEE_BENEFICIARY)
 *   - _validateDocumentCollection (CONSERV_COURT_DOCS, REPPAYEE_SSA_LETTER)
 *
 * v3 CustomerType has `Conservatorship` but folds RepPayee/VAFiduciary into
 * `Other` for the enum.  Until Wave 3 promotes those, we detect via
 * `metadata.fiduciarySubtype`.
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import {
  buildCipGate,
  buildFeatureRestrictionGates,
  buildOfacGate,
  buildRequiredFieldGates,
  buildRiskReviewGate,
  getPrimary,
  productHasDebit,
  productHasOdp,
  type FieldSpec,
} from '../ProceduralGates';

const FIDUCIARY_FIELDS: ReadonlyArray<FieldSpec> = [
  { field: 'firstName', label: 'Protected person / beneficiary name' },
  { field: 'taxId', label: 'Protected person / beneficiary SSN' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

export function runFiduciaryGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  const primary = getPrimary(cart);
  if (!primary) return gates;

  const subtype = typeof cart.metadata?.fiduciarySubtype === 'string'
    ? (cart.metadata.fiduciarySubtype as string)
    : '';

  gates.push(...buildRequiredFieldGates(cart, primary, FIDUCIARY_FIELDS));

  // Restrictions (Conservator)
  if (cart.entityType === 'Conservatorship') {
    gates.push(
      ...buildFeatureRestrictionGates(cart, [
        {
          gateCode: 'CONSERV_NO_ODP',
          label: 'Conservator cannot have ODP',
          predicate: productHasOdp,
          reason: 'Conservator accounts cannot have Overdraft Protection.',
        },
        {
          gateCode: 'CONSERV_NO_DEBIT',
          label: 'Conservator cannot have debit card',
          predicate: productHasDebit,
          reason: 'Conservator accounts cannot have a debit card.',
        },
      ]),
    );

    // Protected person linkage (SPFx CONSERV_PROTECTED)
    if (!primary.conservateeCifId && !primary.protectedPersonCifId) {
      gates.push({
        gateCode: 'CONSERV_PROTECTED',
        entityType: cart.entityType ?? '',
        category: 'relationship',
        status: 'fail',
        label: 'Protected person CIF must be linked',
        reason: 'Conservator accounts require the protected person CIF to be linked.',
        remediationAction: { label: 'Link protected person', deepLinkStepId: 'people' },
      });
    }

    // Court order on file (SPFx CONSERV_COURT_DOCS)
    if (!primary.courtOrderOnFile) {
      gates.push({
        gateCode: 'CONSERV_COURT_DOCS',
        entityType: cart.entityType ?? '',
        category: 'documentCollection',
        status: 'fail',
        label: 'Court order on file',
        reason: 'Conservator accounts require court order documentation on file.',
        remediationAction: { label: 'Upload court order', deepLinkStepId: 'compliance' },
      });
    }
  }

  // RepPayee subtype (v3 metadata flag)
  if (subtype === 'RepPayee') {
    gates.push(
      ...buildFeatureRestrictionGates(cart, [
        {
          gateCode: 'REPPAYEE_NO_DEBIT',
          label: 'Rep Payee cannot have debit card',
          predicate: productHasDebit,
          reason: 'Representative Payee accounts cannot have a debit card.',
        },
      ]),
    );

    if (!primary.protectedPersonCifId) {
      gates.push({
        gateCode: 'REPPAYEE_BENEFICIARY',
        entityType: cart.entityType ?? '',
        category: 'relationship',
        status: 'fail',
        label: 'Beneficiary CIF must be linked',
        reason: 'Representative Payee accounts require the beneficiary CIF to be linked.',
        remediationAction: { label: 'Link beneficiary', deepLinkStepId: 'people' },
      });
    }

    // SSA letter collected
    gates.push({
      gateCode: 'REPPAYEE_SSA_LETTER',
      entityType: cart.entityType ?? '',
      category: 'documentCollection',
      status: primary.extra && primary.extra['ssaLetterCollected'] === true ? 'pass' : 'fail',
      label: 'SSA appointment letter collected',
      reason: 'Rep Payee accounts require the SSA appointment letter to be collected.',
      remediationAction: { label: 'Upload SSA letter', deepLinkStepId: 'compliance' },
    });
  }

  // Risk Management review for all fiduciary subtypes (SPFx WorkflowEngine RISK_MGMT_REVIEW).
  // Status is driven by cart.riskReviewStatus (set by RiskReviewFunction at
  // pipeline step 7→8 — see apps/PCBJxChangeApi/Functions/RiskReviewFunction.cs).
  gates.push(buildRiskReviewGate(
    cart,
    'RISK_MGMT_REVIEW',
    'Risk Management review',
    'authorization',
  ));

  gates.push(buildCipGate(cart));
  gates.push(buildOfacGate(cart));

  return gates;
}
